import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, MessageSquareText, HeartPulse, Inbox, ShieldCheck, Mail, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const USE_AGAIN_COLOR = {
  Definitely: "#4A5D4E",
  Probably: "#6E8A6F",
  "Probably not": "#C4A265",
  No: "#B27A70",
};

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [error, setError] = useState("");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([api.get("/admin/stats"), api.get("/feedback")])
      .then(([s, f]) => {
        setStats(s.data);
        setFeedback(f.data);
      })
      .catch((e) => setError(e.response?.data?.detail || "Could not load admin data."));
  }, [isAdmin]);

  if (user === undefined) return null; // still resolving auth
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <ShieldCheck className="h-10 w-10 text-[#4A5D4E] mx-auto mb-4" strokeWidth={1.5} />
        <h1 className="font-serif text-2xl text-[#2D2A26] mb-2">Admins only</h1>
        <p className="text-[#6E6860]">This page is only available to the Between Us admin account.</p>
      </div>
    );
  }

  const cards = [
    { label: "Registered users", value: stats?.users, icon: Users },
    { label: "Sessions created", value: stats?.sessions, icon: MessageSquareText },
    { label: "Check-ins logged", value: stats?.checkins, icon: HeartPulse },
    { label: "Feedback received", value: stats?.feedback, icon: Inbox },
  ];

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F2EA]">
            <ShieldCheck className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2D2A26]">Admin dashboard</h1>
        </div>
        <p className="text-[#6E6860] mb-8">
          A private overview for the Between Us team. Usage counts only — no couple's private session content is shown here.
        </p>

        {error && (
          <div className="rounded-xl bg-[#B27A70]/10 border border-[#B27A70]/30 p-4 text-sm text-[#8a4f45] mb-8" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="admin-stats">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-[#E8E3D9] p-6 shadow-sm">
              <c.icon className="h-5 w-5 text-[#4A5D4E] mb-3" strokeWidth={1.5} />
              <p className="font-serif text-3xl text-[#2D2A26]">{c.value ?? "—"}</p>
              <p className="text-sm text-[#6E6860] mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        {/* Feedback */}
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-serif text-xl text-[#2D2A26]">Feedback</h2>
          <p className="text-sm text-[#6E6860]">New responses also email michaelmascolo@commongroundinstitute.net</p>
        </div>

        {feedback === null ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-28 rounded-2xl bg-white border border-[#E8E3D9] animate-breathe" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        ) : feedback.length === 0 ? (
          <div className="rounded-2xl bg-[#F5F2EA] border border-dashed border-[#D9C5B2] p-10 text-center">
            <p className="text-[#6E6860]">No feedback yet. When someone submits the feedback form, it will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <div key={f.id} className="rounded-2xl bg-white border border-[#E8E3D9] p-6 shadow-sm" data-testid={`feedback-item-${f.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-4 pb-3 border-b border-[#E8E3D9]">
                  <span className="inline-flex items-center gap-1.5 text-sm text-[#2D2A26]">
                    {f.email ? <Mail className="h-3.5 w-3.5 text-[#4A5D4E]" /> : <UserIcon className="h-3.5 w-3.5 text-[#A39E94]" />}
                    {f.email || "Anonymous (not signed in)"}
                  </span>
                  <span className="text-xs text-[#A39E94]">
                    {f.created_at ? new Date(f.created_at).toLocaleString() : ""} {f.page ? `· ${f.page}` : ""}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                  <QA label="What helped you most?" value={f.helped} />
                  <QA label="Confusing / frustrating / unhelpful?" value={f.confusing} />
                  <QA label="One thing to change" value={f.change} />
                  <div>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#6E6860] mb-1">Would use again?</p>
                    {f.would_use_again ? (
                      <span
                        className="inline-block text-sm font-medium px-3 py-1 rounded-full text-white"
                        style={{ backgroundColor: USE_AGAIN_COLOR[f.would_use_again] || "#6E6860" }}
                      >
                        {f.would_use_again}
                      </span>
                    ) : (
                      <p className="text-sm text-[#A39E94]">—</p>
                    )}
                  </div>
                  {f.other && (
                    <div className="sm:col-span-2">
                      <QA label="Anything else" value={f.other} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}

function QA({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.15em] text-[#6E6860] mb-1">{label}</p>
      <p className="text-sm text-[#2D2A26] leading-relaxed whitespace-pre-wrap">{value || "—"}</p>
    </div>
  );
}
